import { useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
    TextField, Radio, RadioGroup, FormControlLabel, FormControl, FormLabel, Button
} from "@material-ui/core";
import { MuiPickersUtilsProvider, KeyboardDatePicker } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import { es } from "date-fns/locale";
import { useDispatch, useSelector } from "react-redux";
import { offLoadAction, onLoadAnction } from "../../../ducks/loadDuck";
import { FormatFecha } from "../../../utils/FormatFecha";
import {
    nuevoOfertaPaqueteAction,
    obtenerOfertasPaqueteAction,
    actualizarOfertaPaqueteAction,
    closeEditarOfertaPaqueteAction
} from "../../../ducks/ofertasPaqueteDuck";
import { ToastMessage } from "../../../global/ToastMessage";
import { DialogMessage } from "../../../global/DialogMessage";

const validationSchema = Yup.object({
    startDate: Yup.date()
        .required("Fecha de inicio requerida")
        .test("is-less", "La fecha inicio debe ser menor que la fecha de fin", function (value) {
            const { endDate } = this.parent;
            return !endDate || !value || value < endDate;
        }),
    endDate: Yup.date()
        .required("Fecha fin requerida")
        .test("is-greater", "La fecha de fin debe ser mayor que la fecha de inicio", function (value) {
            const { startDate } = this.parent;
            return !startDate || !value || value > startDate;
        }),
    description: Yup.string().required("Descripción requerida"),
    quantity: Yup.number()
        .typeError("Debe ser un número válido")
        .positive("Debe ser un número positivo")
        .when("operation", {
            is: "porcentaje",
            then: Yup.number()
                .integer("Debe ser un número entero")
                .min(1, "El valor debe ser al menos 1")
                .max(100, "El valor debe ser como máximo 100")
                .required("Cantidad requerida"),
            otherwise: Yup.number()
                .test("decimal-places", "Máximo 3 decimales permitidos",
                    (value) => value === undefined || /^\d+(\.\d{1,3})?$/.test(value.toString()))
                .required("Cantidad requerida"),
        }),
    operation: Yup.string().oneOf(["porcentaje", "monto"]).required("Seleccione una operación"),
});

const FormOferta = () => {
    const { clavePaquete, oferta, editar } = useSelector(store => store.ofertasPaquete);
    const dispatch = useDispatch();

    const formik = useFormik({
        initialValues: {
            startDate: null,
            endDate: null,
            description: "",
            quantity: "",
            operation: "porcentaje",
        },
        validationSchema,
        onSubmit: (values) => {
            DialogMessage.fire({
                title: "Confirmar Datos",
                html: `
                    <p><strong>Fecha de inicio:</strong> ${values.startDate?.toLocaleDateString()}</p>
                    <p><strong>Fecha de fin:</strong> ${values.endDate?.toLocaleDateString()}</p>
                    <p><strong>Descripción:</strong> ${values.description}</p>
                    <p><strong>Cantidad:</strong> ${values.quantity}</p>
                    <p><strong>Operación:</strong> ${values.operation === "porcentaje" ? "Porcentaje (%)" : "Monto ($)"}</p>
                `,
                icon: "info",
                showCancelButton: true,
                confirmButtonText: "Confirmar",
                cancelButtonText: "Cancelar",
            }).then((result) => {
                if (result.isConfirmed) {
                    dispatch(onLoadAnction({ message: "" }));

                    let payload = {
                        cantidad: values.quantity,
                        descripcion: values.description,
                        fechaInicio: FormatFecha(values.startDate),
                        fechaFin: FormatFecha(values.endDate),
                        operacion: values.operation === "porcentaje" ? "*" : "-",
                    };

                    if (editar) {
                        // Modo edición: actualizar la oferta
                        dispatch(actualizarOfertaPaqueteAction({ idPaqueteOferta: oferta.IdPaqueteOferta, ...payload }))
                            .then(handleResponse);
                    } else {
                        // Modo creación: crear nueva oferta
                        dispatch(nuevoOfertaPaqueteAction(payload))
                            .then(handleResponse);
                    }
                }
            });
        },
    });

    // Función para manejar la respuesta de la API
    const handleResponse = (Response) => {
        dispatch(offLoadAction())
        if (Response.status === 500) {
            ToastMessage.fire({ icon: "error", title: "Ha habido un problema, intenta más tarde" });
            return;
        }
        if (Response.status === 400) {
            ToastMessage.fire({ icon: "warning", title: Response.Message });
            return;
        }
        dispatch(obtenerOfertasPaqueteAction({ clavePaquete }))
        ToastMessage.fire("¡Guardado!", "Los datos han sido guardados correctamente.", "success");
        formik.resetForm()
        dispatch(closeEditarOfertaPaqueteAction())
    };

    // Cargar datos en modo edición
    useEffect(() => {
        if (editar && oferta) {
            formik.setValues({
                startDate: oferta.FechaInicio ? new Date(oferta.FechaInicio) : null,
                endDate: oferta.FechaFin ? new Date(oferta.FechaFin) : null,
                description: oferta.Descripcion || "",
                quantity: oferta.Cantidad || "",
                operation: oferta.Operacion === "*" ? "porcentaje" : "monto",
            });
        }
    }, [editar, oferta]);

    const handleCancel = () => {
        formik.resetForm()
        dispatch(closeEditarOfertaPaqueteAction())
    }

    return (
        <MuiPickersUtilsProvider utils={DateFnsUtils} locale={es}>
            <form onSubmit={formik.handleSubmit} style={{ maxWidth: 400, margin: "auto" }}>
                <FormControl component="fieldset" margin="normal">
                    <FormLabel component="legend">Operación</FormLabel>
                    <RadioGroup row name="operation" value={formik.values.operation} onChange={formik.handleChange}>
                        <FormControlLabel value="porcentaje" control={<Radio />} label="Porcentaje (%)" />
                        <FormControlLabel value="monto" control={<Radio />} label="Monto ($)" />
                    </RadioGroup>
                </FormControl>

                {/* Cantidad */}
                <TextField
                    fullWidth
                    variant="outlined"
                    type="text"
                    margin="normal"
                    label="Cantidad"
                    name="quantity"
                    value={formik.values.quantity}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.quantity && Boolean(formik.errors.quantity)}
                    helperText={formik.touched.quantity && formik.errors.quantity}
                />

                {/* Fecha de inicio */}
                <KeyboardDatePicker
                    fullWidth
                    inputVariant="outlined"
                    variant="inline"
                    margin="normal"
                    label="Fecha de inicio"
                    format="dd/MM/yyyy"
                    value={formik.values.startDate}
                    onChange={(date) => formik.setFieldValue("startDate", date)}
                    onBlur={formik.handleBlur}
                    error={formik.touched.startDate && Boolean(formik.errors.startDate)}
                    helperText={formik.touched.startDate && formik.errors.startDate}
                    disableToolbar={true}
                />

                {/* Fecha de fin */}
                <KeyboardDatePicker
                    fullWidth
                    inputVariant="outlined"
                    margin="normal"
                    variant="inline"
                    label="Fecha de fin"
                    format="dd/MM/yyyy"
                    value={formik.values.endDate}
                    onChange={(date) => formik.setFieldValue("endDate", date)}
                    onBlur={formik.handleBlur}
                    error={formik.touched.endDate && Boolean(formik.errors.endDate)}
                    helperText={formik.touched.endDate && formik.errors.endDate}
                    disableToolbar={true}
                />

                {/* Descripción */}
                <TextField
                    fullWidth
                    multiline
                    variant="outlined"
                    minRows={3}
                    maxRows={3}
                    margin="normal"
                    label="Descripción"
                    name="description"
                    value={formik.values.description}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.description && Boolean(formik.errors.description)}
                    helperText={formik.touched.description && formik.errors.description}
                />

                {/* Botón de enviar */}
                <div style={{display: "flex", justifyContent: "center", alignItems: "center", gap: "10px"}}>
                    {
                        editar && (
                            <Button onClick={handleCancel} variant="contained" color="inherit" fullWidth >
                                Cancelar
                            </Button>
                        )
                    }
                    <Button type="submit" variant="contained" color="primary" fullWidth>
                        {editar ? "Actualizar" : "Guardar"}
                    </Button>
                </div>

            </form>
        </MuiPickersUtilsProvider>
    );
};

export default FormOferta;
